import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, KeyRound, UserRound, UserX } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errMessage, errFields } from "@/lib/api";
import { PageTitle, Section, InfoItem, FieldError } from "@/components/common";
import { Avatar } from "@/components/Layout";
import { roleLabel } from "@/lib/labels";
import { WorkplaceSwitcher, JoinWorkplace } from "@/components/WorkplaceSwitcher";
import { ProfileForm } from "@/components/ProfileForm";

const FIELDS = [
  ["current_password", "Mot de passe actuel", "current-password"],
  ["new_password", "Nouveau mot de passe (8 caractères minimum)", "new-password"],
  ["confirm", "Confirmer le nouveau mot de passe", "new-password"],
];

export const ChangePassword = ({ onDone, title = "Changer mon mot de passe", currentLabel }) => {
  const empty = { current_password: "", new_password: "", confirm: "" };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.current_password) errs.current_password = "Saisissez votre mot de passe actuel.";
    if (form.new_password.length < 8) errs.new_password = "Le mot de passe doit contenir au moins 8 caractères.";
    else if (form.new_password === form.current_password) errs.new_password = "Choisissez un mot de passe différent de l'actuel.";
    if (form.confirm !== form.new_password) errs.confirm = "Les deux mots de passe ne correspondent pas.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success(data.message);
      setForm(empty);
      onDone?.();
    } catch (err) {
      const f = errFields(err);
      setErrors(Object.keys(f).length ? f : { form: errMessage(err) });
    } finally { setBusy(false); }
  };
  return (
    <Section title={title} icon={KeyRound} testId="profile-password-section">
      <form onSubmit={submit} className="max-w-md space-y-4">
        {FIELDS.map(([k, label, auto]) => (
          <div key={k}>
            <label htmlFor={`pw-${k}`} className="text-sm font-semibold">{k === "current_password" && currentLabel ? currentLabel : label}</label>
            <Input id={`pw-${k}`} data-testid={`profile-${k}-input`} type="password" autoComplete={auto} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5 h-12 text-base" />
            <FieldError error={errors[k]} testId={`profile-${k}-error`} />
          </div>
        ))}
        <FieldError error={errors.form} testId="profile-password-error" />
        <p className="text-xs text-muted-foreground">Après le changement, vos autres sessions (autres appareils) seront déconnectées.</p>
        <Button data-testid="profile-password-submit" type="submit" disabled={busy} className="h-12 w-full font-bold sm:w-auto">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Enregistrer le nouveau mot de passe
        </Button>
      </form>
    </Section>
  );
};

export default function Profile() {
  const { user } = useAuth();
  return (
    <div className="space-y-5">
      <PageTitle kicker="Compte" title="Mon profil" testId="profile-title" />
      <Section title="Mes informations" icon={UserRound} testId="profile-info-section">
        <div className="mb-5 flex flex-wrap items-center gap-5">
          <Avatar name={user.name} />
          <div className="grid flex-1 gap-4 sm:grid-cols-2">
            <InfoItem label="Rôle" value={roleLabel(user)} testId="profile-role" />
            <InfoItem label="Organisation" value={user.org_name} testId="profile-org" />
          </div>
        </div>
        <ProfileForm initial={user} url="/auth/profile" email={user.email} onSaved={() => setTimeout(() => window.location.reload(), 1200)} />
      </Section>
      <Section title="Mes workplaces" icon={UserRound} testId="profile-workplaces-section">
        <WorkplaceSwitcher testId="profile-workplace-switcher" always />
        <p className="mb-2 mt-4 text-sm text-muted-foreground">Rejoindre un autre workplace avec son code (vous y serez Opérateur ; son administrateur peut changer votre rôle).</p>
        <JoinWorkplace />
      </Section>
      <Link to="/equipes" data-testid="profile-teams-link" className="flex items-center justify-between rounded-xl border border-border bg-card p-4 font-semibold hover:border-amber-500/50">Mes équipes <span className="text-amber-500">→</span></Link>
      {user?.platform_admin && <Link to="/plateforme" data-testid="profile-platform-link" className="flex items-center justify-between rounded-xl border border-border bg-card p-4 font-semibold hover:border-amber-500/50">Administration de la plateforme <span className="text-amber-500">→</span></Link>}
      <ChangePassword />
      {!user.platform_admin && <DeleteAccount requested={user.deletion_requested} />}
    </div>
  );
}

const DeleteAccount = ({ requested }) => {
  const [done, setDone] = useState(requested);
  const ask = async () => {
    if (!window.confirm("Demander la suppression de votre compte ? Vos Take 5 et inspections sont conservés pendant la durée de conservation des données.")) return;
    try {
      await api.post("/auth/delete-request");
      setDone(true);
      toast.success("Demande de suppression envoyée à l'administrateur de la plateforme.");
    } catch (e) { toast.error(errMessage(e)); }
  };
  return (
    <Section title="Supprimer mon compte" icon={UserX} testId="profile-delete-section">
      <p className="text-sm text-muted-foreground">Votre compte sera anonymisé par l'administrateur de la plateforme. Vos Take 5 et inspections restent conservés pendant la durée légale de conservation des données.</p>
      {done ? <p data-testid="profile-delete-requested" className="mt-3 font-semibold text-amber-500">Demande de suppression enregistrée.</p>
        : <Button variant="outline" data-testid="profile-delete-request" onClick={ask} className="mt-3 border-red-500/60 text-red-500 hover:bg-red-500/10">Demander la suppression de mon compte</Button>}
    </Section>
  );
};
